/**
 * ProjectPeopleLabelsSettings — Feb 2026 R293k
 *
 * Workspace-admin surface for the Project People Labels catalogue.
 * Flat list, drag-reorder not required for the MVP (sort uses the
 * `position` field so we can add d&d later without a schema change).
 *
 * - Create / rename / delete labels (workspace scoped)
 * - Preview chip colour inline
 * - Delete removes the label from every project's `people_labels` map
 *   on the backend so the Projects list stays clean.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Users, Plus, Trash2, Pencil, ArrowLeft, GripVertical } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";

const COLOR_SWATCHES = [
  { label: "Slate",   cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { label: "Sky",     cls: "bg-sky-100 text-sky-700 border-sky-300" },
  { label: "Emerald", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { label: "Amber",   cls: "bg-amber-100 text-amber-700 border-amber-300" },
  { label: "Rose",    cls: "bg-rose-100 text-rose-700 border-rose-300" },
  { label: "Violet",  cls: "bg-violet-100 text-violet-700 border-violet-300" },
];

export default function ProjectPeopleLabelsSettings() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // row object or null
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLOR_SWATCHES[0].cls);

  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/project-people-roles`);
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load People Labels");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const createRow = async () => {
    const name = (newName || "").trim();
    if (!name) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/project-people-roles`, { name, color: newColor });
      toast.success(`Added label "${name}"`);
      setNewName("");
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    if (!name) return;
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/project-people-roles/${editing.id}`, {
        name, color: editing.color || null,
      });
      toast.success("Label updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Assignments on every project will be cleared.`)) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/project-people-roles/${row.id}`);
      toast.success("Label deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // Feb 2026 — Drag-reorder support. Reordering updates the local
  // array optimistically, then POSTs the new order to the bulk reorder
  // endpoint. The catalogue is the source of truth for both the
  // Projects list column order AND the Overview picker, so this
  // propagates everywhere once the server responds.
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const persistOrder = async (nextRows) => {
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/project-people-roles/reorder`,
        { ids: nextRows.map((r) => r.id) },
      );
      setRows(data || nextRows);
      toast.success("Order saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
      await load();
    }
  };
  const onDragStart = (id) => (e) => {
    setDragId(id);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (dragId && dragId !== id) setDragOverId(id);
  };
  const onDrop = (id) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === id) { setDragId(null); setDragOverId(null); return; }
    const next = rows.slice();
    const fromIdx = next.findIndex((r) => r.id === dragId);
    const toIdx = next.findIndex((r) => r.id === id);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); setDragOverId(null); return; }
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setRows(next);
    setDragId(null);
    setDragOverId(null);
    persistOrder(next);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  return (
    <div className="pb-10" data-testid="people-labels-settings">
      <PageHeader
        eyebrow={
          <Link to="/app/settings" className="inline-flex items-center gap-1 label-eyebrow hover:text-primary">
            <ArrowLeft className="w-3 h-3" /> Workspace settings
          </Link>
        }
        title="Project people labels"
        description="Define the role chips your team pins to every project. Any workspace member can be assigned to any label."
        compact
      />
      <div className="px-4 sm:px-6 md:px-10 pt-4 pb-16">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <Users className="w-3 h-3" /> {rows.length} label{rows.length === 1 ? "" : "s"}
            {rows.length > 1 && (
              <span className="text-[10px] normal-case tracking-normal text-muted-foreground ml-2">
                · drag to reorder — order drives the Projects list columns and Overview picker
              </span>
            )}
          </div>
          <Button size="sm" className="rounded-none" onClick={() => setCreating(true)}
                  data-testid="people-labels-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add label
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center">
            <Users className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
            <div className="text-sm font-medium">No labels yet</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
              Add labels like "Account Manager" or "Creative Lead" — each one becomes a column on the Projects list and a picker on every project.
            </p>
            <Button size="sm" variant="outline" className="rounded-none mt-4" onClick={() => setCreating(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add first label
            </Button>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {rows.map((row) => (
              <div key={row.id}
                   draggable
                   onDragStart={onDragStart(row.id)}
                   onDragOver={onDragOver(row.id)}
                   onDrop={onDrop(row.id)}
                   onDragEnd={onDragEnd}
                   className={`flex items-center gap-3 p-3 group transition-colors ${
                     dragId === row.id ? "opacity-50" : ""
                   } ${
                     dragOverId === row.id ? "bg-primary/5 ring-1 ring-primary/40" : "hover:bg-muted/30"
                   }`}
                   data-testid={`people-label-row-${row.id}`}>
                <span className="cursor-grab active:cursor-grabbing text-muted-foreground group-hover:text-foreground select-none"
                      title="Drag to reorder"
                      data-testid={`people-label-drag-${row.id}`}>
                  <GripVertical className="w-4 h-4" />
                </span>
                <span className={`inline-block border px-2 py-0.5 text-xs ${row.color || "bg-muted/40 border-border"}`}>
                  {row.name}
                </span>
                <span className="text-[10px] text-muted-foreground font-mono">#{row.position}</span>
                <div className="ml-auto flex gap-1 opacity-70 group-hover:opacity-100">
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-xs"
                          onClick={() => setEditing({ ...row })}
                          data-testid={`people-label-edit-${row.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-xs text-rose-700 hover:bg-rose-50"
                          onClick={() => remove(row)}
                          data-testid={`people-label-delete-${row.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-none max-w-md" data-testid="people-labels-create-dialog">
          <DialogHeader>
            <DialogTitle>New people label</DialogTitle>
            <DialogDescription>
              Pick a short, descriptive name (max 40 chars).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="label-eyebrow mb-1 block">Label name</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                     placeholder="e.g. Account Manager" className="rounded-none"
                     data-testid="people-labels-new-name" autoFocus
                     onKeyDown={(e) => { if (e.key === "Enter") createRow(); }} />
            </div>
            <ColorPicker value={newColor} onChange={setNewColor} testidBase="people-labels-new-color" />
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={createRow}
                     disabled={!newName.trim()}
                     data-testid="people-labels-new-save">
              Create label
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="people-labels-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit label</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="label-eyebrow mb-1 block">Label name</label>
              <Input value={editing?.name || ""}
                     onChange={(e) => setEditing((cur) => cur ? { ...cur, name: e.target.value } : cur)}
                     className="rounded-none" autoFocus
                     onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }} />
            </div>
            <ColorPicker value={editing?.color} onChange={(c) => setEditing((cur) => cur ? { ...cur, color: c } : cur)} testidBase="people-labels-edit-color" />
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveEdit} disabled={!(editing?.name || "").trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ColorPicker({ value, onChange, testidBase }) {
  return (
    <div>
      <label className="label-eyebrow mb-1 block">Chip colour</label>
      <div className="flex flex-wrap gap-1">
        {COLOR_SWATCHES.map((s) => (
          <button
            type="button"
            key={s.label}
            onClick={() => onChange(s.cls)}
            className={`border px-2 py-0.5 text-xs ${s.cls} ${value === s.cls ? "ring-2 ring-primary" : ""}`}
            data-testid={`${testidBase}-${s.label.toLowerCase()}`}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
